// Pure helpers for the Analytics module.
// Kept free of React so the correctness rules can be unit tested on their own.

export const SENTIMENT_POSITIVE = 'POSITIVE';
export const SENTIMENT_NEGATIVE = 'NEGATIVE';
export const SENTIMENT_NEUTRAL = 'NEUTRAL';

export const VALUE_KIND_STOCK = 'STOCK';
export const VALUE_KIND_FLOW = 'FLOW';

// Colour is driven by the server supplied sentiment only. The sign of the delta
// says nothing about whether the movement is good or bad - corrupted devices
// going up is a NEGATIVE movement even though the number rose.
export const getSentimentClass = (sentiment) => {
  switch (sentiment) {
    case SENTIMENT_POSITIVE:
      return 'analytics-sentiment-positive';
    case SENTIMENT_NEGATIVE:
      return 'analytics-sentiment-negative';
    default:
      return 'analytics-sentiment-neutral';
  }
};

// A delta is only shown when the server says the two differenced dates are
// consecutive present days. A gap in the nightly rollups must never be
// rendered as a real movement.
export const isDeltaDisplayable = (card) =>
  card?.deltaReliable === true &&
  card?.deltaAbsolute !== null &&
  card?.deltaAbsolute !== undefined;

export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toLocaleString('en-US');
};

export const formatDelta = (card) => {
  const absolute = Number(card?.deltaAbsolute);
  const sign = absolute > 0 ? '+' : '';
  const percent = card?.deltaPercent;
  const percentText =
    percent === null || percent === undefined ? '' : ` (${sign}${Number(percent).toFixed(2)}%)`;
  return `${sign}${formatNumber(absolute)}${percentText}`;
};

export const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Default window is the last 30 days inclusive of today.
export const defaultDateRange = (today = new Date()) => {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  from.setDate(from.getDate() - 29);
  return { fromDate: toIsoDate(from), toDate: toIsoDate(today) };
};

export const MAX_RANGE_DAYS = 400;

export const validateDateRange = (fromDate, toDate) => {
  if (!fromDate || !toDate) {
    return 'Both a from date and a to date are required.';
  }
  if (toDate < fromDate) {
    return 'The to date must be on or after the from date.';
  }
  const spanDays =
    Math.round((new Date(toDate).getTime() - new Date(fromDate).getTime()) / 86400000) + 1;
  if (spanDays > MAX_RANGE_DAYS) {
    return `The date range is capped at ${MAX_RANGE_DAYS} days.`;
  }
  return null;
};

export const getDimensionValues = (points = []) =>
  Array.from(
    new Set(points.map((point) => point.dimension).filter((value) => value !== null && value !== undefined))
  ).sort();

// Collapses the per-dimension rows into one value per date so the series can be
// drawn with the shared DashboardChart wrapper (single dataset).
export const aggregateSeriesByDate = (points = []) => {
  const totals = points.reduce((acc, point) => {
    acc[point.date] = (acc[point.date] || 0) + Number(point.value || 0);
    return acc;
  }, {});

  return Object.keys(totals)
    .sort()
    .map((date) => ({ label: date, data: totals[date] }));
};

// A STOCK row is a standing total as of that date, so adding the dates together
// produces a number that means nothing. Only FLOW datasets may be summed.
export const canShowPeriodTotal = (valueKind) => valueKind === VALUE_KIND_FLOW;

export const sumSeries = (aggregated = []) =>
  aggregated.reduce((total, entry) => total + Number(entry.data || 0), 0);

export const latestSeriesPoint = (aggregated = []) =>
  aggregated.length > 0 ? aggregated[aggregated.length - 1] : null;

export const isCoverageIncomplete = (coverage) => !!coverage && coverage.complete === false;

export const describeCoverageGaps = (coverage) => {
  const gaps = coverage?.gapDates || [];
  if (gaps.length === 0) {
    return 'Some days in this range have no nightly rollup.';
  }
  return `No nightly rollup for: ${gaps.join(', ')}.`;
};
